import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import hero640 from "@/assets/hero-presenca-640.webp";
import hero1024 from "@/assets/hero-presenca-1024.webp";
import hero1600 from "@/assets/hero-presenca-1600.webp";
import hero1920 from "@/assets/hero-presenca-1920.webp";
import {
  Cross,
  User,
  Heart,
  Baby,
  Briefcase,
  ArrowRight,
  Instagram,
  Youtube,
  Facebook,
} from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Presença Que Marca — Homens que deixam marcas eternas" },
      {
        name: "description",
        content:
          "Ajudamos homens cristãos a fortalecer sua relação com Deus, identidade, casamento, filhos e liderança para viver uma vida de propósito, influência e legado.",
      },
      { property: "og:title", content: "Presença Que Marca — Homens que deixam marcas eternas" },
      {
        property: "og:description",
        content:
          "Fé, família e legado. Uma missão para homens cristãos que desejam viver com presença, propósito e influência.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://presencaquemarca.com.br/" },
      { property: "og:image", content: `https://presencaquemarca.com.br${hero1600}` },
      { name: "twitter:image", content: `https://presencaquemarca.com.br${hero1600}` },
    ],
    links: [
      { rel: "canonical", href: "https://presencaquemarca.com.br/" },
      {
        rel: "preload",
        as: "image",
        href: hero1024,
        imageSrcSet: `${hero640} 640w, ${hero1024} 1024w, ${hero1600} 1600w, ${hero1920} 1920w`,
        imageSizes: "100vw",
        fetchPriority: "high",
      },
    ],
  }),
  component: HomePage,
});

const pillars = [
  { icon: Cross, title: "Deus", text: "A presença que fortalece sua fé." },
  { icon: User, title: "Identidade", text: "A presença que forma o homem." },
  { icon: Heart, title: "Casamento", text: "A presença que constrói alianças." },
  { icon: Baby, title: "Filhos", text: "A presença que deixa legado." },
  { icon: Briefcase, title: "Negócios", text: "A presença que lidera com propósito." },
];

function HomePage() {
  return (
    <div className="min-h-screen bg-[var(--navy-deep)] text-ivory antialiased">
      {/* Nav */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-10">
          <a href="#top" className="font-display text-base tracking-[0.25em] text-ivory">
            PRESENÇA <span className="text-gold">QUE</span> MARCA
          </a>
          <nav className="hidden gap-8 text-xs uppercase tracking-[0.2em] text-ivory/70 md:flex">
            <a href="#manifesto" data-event="cta_manifesto" className="hover:text-gold transition-colors">Manifesto</a>
            <a href="#pilares" data-event="cta_pilares" className="hover:text-gold transition-colors">Pilares</a>
            <a href="#programas" data-event="cta_programas" className="hover:text-gold transition-colors">Programas</a>
            <a href="#sobre" data-event="cta_sobre" className="hover:text-gold transition-colors">Sobre</a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section id="top" className="relative min-h-[100svh] w-full overflow-hidden">
        <img
          src={hero1024}
          srcSet={`${hero640} 640w, ${hero1024} 1024w, ${hero1600} 1600w, ${hero1920} 1920w`}
          sizes="100vw"
          alt="Homem caminhando em direção ao horizonte ao nascer do sol"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy-deep)]/85 via-[var(--navy-deep)]/55 to-[var(--navy-deep)]" />
        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-6xl flex-col justify-end px-6 pb-20 pt-40 md:px-10 md:pb-28">
          <div className="max-w-3xl animate-fade-up">
            <div className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-gold">
              <span className="h-px w-10 bg-gold" />
              Fé • Família • Legado
            </div>
            <h1 className="font-display text-5xl leading-[1.05] text-ivory md:text-7xl lg:text-8xl">
              Homens que deixam
              <br />
              <span className="text-gradient-gold italic">marcas eternas.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/80 md:text-lg">
              Ajudamos homens cristãos a fortalecer sua relação com Deus, sua identidade, seu casamento, seus filhos e sua liderança — para viver uma vida de propósito, influência e legado.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <a
                href="https://presencaquemarca.com.br/paimemoravel"
                data-event="cta_comecar_agora"
                className="group inline-flex items-center justify-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-[var(--navy-deep)] transition-all hover:bg-[var(--gold-soft)] shadow-gold"
              >
                Começar Agora
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#programas"
                data-event="cta_conhecer_programas"
                className="inline-flex items-center justify-center border border-ivory/30 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ivory transition-all hover:border-gold hover:text-gold"
              >
                Conhecer os Programas
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="relative bg-[var(--navy-deep)] py-32 md:py-48">
        <div className="mx-auto max-w-4xl px-6 text-center md:px-10">
          <div className="mb-10 inline-flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-gold">
            <span className="h-px w-10 bg-gold" />
            Manifesto
            <span className="h-px w-10 bg-gold" />
          </div>
          <h2 className="font-display text-4xl leading-tight text-ivory md:text-6xl">
            O mundo não precisa de homens <span className="italic text-gold/90">mais ocupados.</span>
          </h2>
          <div className="mx-auto mt-16 max-w-2xl space-y-5 text-lg leading-relaxed text-ivory/80 md:text-xl">
            <p>Precisa de homens mais presentes.</p>
            <p>Presentes com Deus.</p>
            <p>Presentes consigo mesmos.</p>
            <p>Presentes com suas esposas.</p>
            <p>Presentes com seus filhos.</p>
            <p>Presentes em sua missão.</p>
            <div className="mx-auto my-8 h-px w-24 bg-gold/60" />
            <p className="font-display text-2xl italic text-ivory md:text-3xl">
              Porque toda presença deixa uma marca.
            </p>
            <p className="font-display text-2xl italic text-gold md:text-3xl">
              E toda ausência também.
            </p>
          </div>
        </div>
      </section>

      {/* PILARES */}
      <section id="pilares" className="relative bg-[var(--navy)] py-32 md:py-40">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mx-auto mb-20 max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-gold">
              <span className="h-px w-10 bg-gold" />
              Cinco Pilares
            </div>
            <h2 className="font-display text-4xl leading-tight text-ivory md:text-6xl">
              Os pilares da <span className="italic text-gold">Presença Que Marca</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {pillars.map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                className="group relative border border-ivory/10 bg-[var(--navy-deep)]/40 p-8 transition-all hover:border-gold/60 hover:bg-[var(--navy-deep)]/70"
              >
                <div className="mb-6 inline-flex h-14 w-14 items-center justify-center border border-gold/40 text-gold transition-all group-hover:bg-gold group-hover:text-[var(--navy-deep)]">
                  <Icon className="h-6 w-6" />
                </div>
                <div className="mb-2 text-xs uppercase tracking-[0.25em] text-gold/90">0{i + 1}</div>
                <h3 className="font-display text-2xl text-ivory">{title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ivory/70">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMAS */}
      <section id="programas" className="relative bg-[var(--navy-deep)] py-32 md:py-40">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="mb-20 text-center">
            <div className="mb-6 inline-flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-gold">
              <span className="h-px w-10 bg-gold" />
              Programas
              <span className="h-px w-10 bg-gold" />
            </div>
            <h2 className="font-display text-4xl text-ivory md:text-6xl">
              Caminhos para uma <span className="italic text-gold">presença intencional</span>
            </h2>
          </div>

          <div className="grid gap-8 lg:grid-cols-2">
            {/* Card 1 - destaque */}
            <div className="group relative overflow-hidden border border-gold/40 bg-gradient-to-br from-[var(--navy)] to-[var(--navy-deep)] p-10 md:p-12 shadow-premium">
              <div className="absolute right-0 top-0 bg-gold px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-[var(--navy-deep)]">
                Disponível
              </div>
              <div className="mb-6 text-xs uppercase tracking-[0.25em] text-gold">Programa Principal</div>
              <h3 className="font-display text-3xl leading-tight text-ivory md:text-4xl">
                O Pai que Seu Filho<br />Vai Lembrar™
              </h3>
              <p className="mt-6 text-base leading-relaxed text-ivory/75">
                Um treinamento para empresários cristãos que desejam construir uma relação forte, admirada e memorável com seus filhos.
              </p>
              <Link
                to="/paimemoravel"
                data-event="cta_comprar_pai_memoravel"
                className="group/btn mt-10 inline-flex items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-[var(--navy-deep)] transition-all hover:bg-[var(--gold-soft)]"
              >
                Saiba Mais
                <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
              </Link>
            </div>

            {/* Card 2 - em breve */}
            <div className="relative overflow-hidden border border-ivory/15 bg-[var(--navy)]/40 p-10 md:p-12">
              <div className="absolute right-0 top-0 bg-ivory/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-ivory/70">
                Em Breve
              </div>
              <div className="mb-6 text-xs uppercase tracking-[0.25em] text-ivory/70">Mentoria</div>
              <h3 className="font-display text-3xl leading-tight text-ivory md:text-4xl">
                Homem de Aliança™
              </h3>
              <p className="mt-6 text-base leading-relaxed text-ivory/70">
                Mentoria para homens que desejam alinhar fé, família, propósito e liderança.
              </p>
              <button
                disabled
                className="mt-10 inline-flex items-center gap-3 border border-ivory/30 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ivory/80"
              >
                Em Breve
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SOBRE */}
      <section id="sobre" className="relative bg-[var(--navy)] py-32 md:py-40">
        <div className="mx-auto max-w-4xl px-6 md:px-10">
          <div className="mb-10 text-center">
            <div className="mb-6 inline-flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-gold">
              <span className="h-px w-10 bg-gold" />
              A História
            </div>
            <h2 className="font-display text-4xl leading-tight text-ivory md:text-5xl">
              Quem está por trás da <span className="italic text-gold">Presença Que Marca?</span>
            </h2>
          </div>
          <div className="mx-auto max-w-2xl space-y-6 text-lg leading-relaxed text-ivory/80">
            <p>
              Durante anos observei homens construindo empresas sólidas enquanto suas famílias enfraqueciam.
            </p>
            <p>Descobri que o problema não era falta de amor.</p>
            <p className="font-display text-xl italic text-gold">
              Era falta de presença intencional.
            </p>
            <p>Foi assim que nasceu a Presença Que Marca.</p>
            <p>
              Uma missão para ajudar homens a viverem uma vida alinhada com Deus, sua família e seu propósito.
            </p>
          </div>
        </div>
      </section>

      {/* FRASE DE IMPACTO */}
      <section className="relative flex min-h-[80vh] items-center justify-center bg-[var(--navy-deep)] px-6 py-32 md:px-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(201,168,76,0.12),_transparent_60%)]" />
        <div className="relative mx-auto max-w-4xl text-center">
          <p className="font-display text-3xl leading-[1.2] text-ivory md:text-5xl lg:text-6xl">
            O patrimônio que você constrói<br />pode ser herdado.
          </p>
          <div className="mx-auto my-10 h-px w-24 bg-gold" />
          <p className="font-display text-3xl italic leading-[1.2] text-gradient-gold md:text-5xl lg:text-6xl">
            As marcas que você deixa<br />serão lembradas.
          </p>
        </div>
      </section>

      {/* CHAMADA FINAL */}
      <section className="relative bg-gradient-to-b from-[var(--navy)] to-[var(--navy-deep)] py-32 md:py-40">
        <div className="mx-auto max-w-3xl px-6 text-center md:px-10">
          <h2 className="font-display text-4xl leading-tight text-ivory md:text-6xl">
            Que marca sua presença<br />está <span className="italic text-gold">deixando?</span>
          </h2>
          <p className="mt-8 text-lg leading-relaxed text-ivory/75">
            A transformação começa quando você decide estar verdadeiramente presente.
          </p>
          <a
            href="https://presencaquemarca.com.br/paimemoravel"
            data-event="cta_comprar_pai_memoravel"
            className="group mt-12 inline-flex items-center gap-3 bg-gold px-10 py-5 text-xs font-semibold uppercase tracking-[0.25em] text-[var(--navy-deep)] transition-all hover:bg-[var(--gold-soft)] shadow-gold"
          >
            Começar pelo Pai Memorável
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-ivory/10 bg-[var(--navy-deep)] py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="grid gap-12 md:grid-cols-3 md:gap-8">
            <div>
              <div className="font-display text-lg tracking-[0.25em] text-ivory">
                PRESENÇA <span className="text-gold">QUE</span> MARCA
              </div>
              <p className="mt-3 text-xs uppercase tracking-[0.3em] text-gold/80">
                Fé • Família • Legado
              </p>
            </div>
            <div>
              <div className="mb-4 text-xs uppercase tracking-[0.25em] text-ivory/70">Navegação</div>
              <ul className="space-y-2 text-sm text-ivory/80">
                <li><a href="#top" className="hover:text-gold transition-colors">Início</a></li>
                <li><a href="#programas" className="hover:text-gold transition-colors">Programas</a></li>
                <li><a href="#sobre" className="hover:text-gold transition-colors">Sobre</a></li>
                <li><a href="mailto:contato@presencaquemarca.com.br" className="hover:text-gold transition-colors">Contato</a></li>
              </ul>
            </div>
            <div>
              <div className="mb-4 text-xs uppercase tracking-[0.25em] text-ivory/70">Conecte-se</div>
              <div className="flex gap-4">
                <a aria-label="Instagram" href="https://www.instagram.com/danielfraron" target="_blank" rel="noopener noreferrer" className="border border-ivory/20 p-3 text-ivory/70 transition-all hover:border-gold hover:text-gold">
                  <Instagram className="h-4 w-4" />
                </a>
                <a aria-label="YouTube" href="https://www.youtube.com/@danielfraron" target="_blank" rel="noopener noreferrer" className="border border-ivory/20 p-3 text-ivory/70 transition-all hover:border-gold hover:text-gold">
                  <Youtube className="h-4 w-4" />
                </a>
                <a aria-label="Facebook" href="https://www.facebook.com/danielfrarao" target="_blank" rel="noopener noreferrer" className="border border-ivory/20 p-3 text-ivory/70 transition-all hover:border-gold hover:text-gold">
                  <Facebook className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-ivory/10 pt-8 text-center text-xs text-ivory/40">
            © {new Date().getFullYear()} Presença Que Marca. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
